// TODO: Making these localizedStrings for non-english languages
// Index of modal error snippets derived from https://www.notion.so/glideapps/Error-codes-f6bac5a8a5464afa903995a603019681

import { ModalBody } from "./modal-v2-common-styles";
import { getDocURL } from "common/docUrl";
import { getNamedIcon } from "common/named-icons";
import copy from "copy-to-clipboard";

interface ModalSnippet {
    title: string;
    bodyContent: React.ReactNode;
}

const CopyLinkIcon = getNamedIcon("copyLink");
export const DefaultErrorMessage = (
    <div className="description">
        If you continue to experience this issue, please send an email to{" "}
        <a
            className="quicklink"
            href={`mailto:support@glideapps.com`}
            title="email support@glideapps.com"
            target="_blank"
            rel="noopener noreferrer">
            support@glideapps.com
        </a>{" "}
        that includes a detailed explanation or a screen recording of the events that lead up to the error message you
        have received. You can use{" "}
        <a
            className="quicklink"
            href={`https://loom.com`}
            title="https://loom.com"
            target="_blank"
            rel="noopener noreferrer">
            loom.com
        </a>{" "}
        or any other screen recording software.
    </div>
);
export const UnableToPerformAction: ModalSnippet = {
    title: "Error",
    bodyContent: (
        <ModalBody>
            <div className="description">
                Unable to perform action. Contact Organization Admin to perform this action.
            </div>
        </ModalBody>
    ),
};

export const InviteUserError: ModalSnippet = {
    title: "Oh Sheet - Could not invite the user",
    bodyContent: (
        <ModalBody>
            <div className="description">
                Make sure the user you are trying to invite to become a member of your Team has a Glide account.
            </div>
        </ModalBody>
    ),
};

export const GenericOhSheetError = (errorMessage?: string | undefined): ModalSnippet => {
    return {
        title: "Oh Sheet!",
        bodyContent: (
            <ModalBody>
                {DefaultErrorMessage}
                <br></br>
                {errorMessage ?? ""}
            </ModalBody>
        ),
    };
};

export const SignInError: ModalSnippet = {
    title: "Oh Sheet - You aren't signed in.",
    bodyContent: (
        <ModalBody>
            <div className="description">
                In order to preform this operation you need to be signed into Glide in the same browser instance.
            </div>
            <div className="description">
                1. Open a browser tab and go to{" "}
                <a
                    className="quicklink"
                    href={`https://go.glideapps.com/`}
                    title="https://go.glideapps.com/"
                    target="_blank"
                    rel="noopener noreferrer">
                    https://go.glideapps.com
                </a>
            </div>
            <div className="description">2. Log into your Glide account and try again</div>
            <br></br>
        </ModalBody>
    ),
};

export const SupportLinkCreateError: ModalSnippet = {
    title: "Oh Sheet - Could not make support link.",
    bodyContent: (
        <ModalBody>
            <div className="description">
                Please send an email to{" "}
                <a
                    className="quicklink"
                    href={`mailto:support@glideapps.com`}
                    title="email support@glideapps.com"
                    target="_blank"
                    rel="noopener noreferrer">
                    support@glideapps.com
                </a>{" "}
                if you encounter this issue.
            </div>
        </ModalBody>
    ),
};

export const AppDuplicationError = (message?: string | undefined): ModalSnippet => {
    return {
        title: "An error occurred duplicating the app.",
        bodyContent: (
            <ModalBody>
                <div className="description">
                    In most cases this is caused by a large and/or complex Google sheet. Delete all rows and columns
                    that your app is not using or there is no data. Glide will append rows as needed so you can remove
                    all empty rows below the existing data on all of your sheets. Then try to duplicate the app again.
                </div>
                <br></br>
                {message !== undefined ? `: ${message}` : ""}
            </ModalBody>
        ),
    };
};

export const CreateSupportLink = (url: string): ModalSnippet => {
    return {
        title: "Copy Support Link",
        bodyContent: (
            <ModalBody>
                <div className="description">
                    Please share this link with Glide Support to grant temporary access to your app:
                </div>
                <span
                    className="applink"
                    onClick={() => {
                        copy(url);
                    }}>
                    <p>{url}</p>
                    <CopyLinkIcon />
                </span>
                <div className="description">
                    Support is not available for Personal apps. If you need help with your app, please visit the{" "}
                    <a
                        className="quicklink "
                        href={`https://community.glideapps.com`}
                        title="community"
                        target="_blank"
                        rel="noopener noreferrer">
                        Community Forum
                    </a>
                    .{" "}
                    <a
                        className="quicklink learnmore"
                        href={getDocURL("supportCodes")}
                        title="learn more"
                        target="_blank"
                        rel="noopener noreferrer">
                        Learn more about support links
                    </a>
                    .
                </div>
            </ModalBody>
        ),
    };
};

export const UnpublishDeleteAppError = (url: string): ModalSnippet => {
    return {
        title: "Oh Sheet - An error occurred unpublishing/deleting the app.",
        bodyContent: (
            <ModalBody>
                <div className="description">
                    For more support, send an email to{" "}
                    <a
                        className="quicklink"
                        href={`mailto:support@glideapps.com`}
                        title="email support@glideapps.com"
                        target="_blank"
                        rel="noopener noreferrer">
                        support@glideapps.com
                    </a>{" "}
                    that includes a detailed explanation or a screen recording of the events that lead up to the error
                    message you have received. You can use{" "}
                    <a
                        className="quicklink"
                        href={`https://loom.com`}
                        title="https://loom.com"
                        target="_blank"
                        rel="noopener noreferrer">
                        loom.com
                    </a>{" "}
                    or any other screen recording software.
                </div>
                <br></br>
                {url && (
                    <div className="description">
                        Please share this link with Glide Support to grant temporary access to your app:
                    </div>
                )}
                {url && (
                    <span
                        className="applink"
                        onClick={() => {
                            copy(url);
                        }}>
                        <p>{url}</p>
                        <CopyLinkIcon />
                    </span>
                )}
            </ModalBody>
        ),
    };
};

export const TransferAppError: ModalSnippet = {
    title: "Error - Could not complete app transfer",
    bodyContent: (
        <ModalBody>
            <div className="description">
                Please go to the Billing folder of the destination team you are trying to transfer the app to has a
                payment method set up on the team. Attempting to transfer a Paid plan into a folder that has no payment
                method will cause this error.
                <div className="description">
                    If you continue to experience issues with transferring your app send an email to{" "}
                    <a
                        className="quicklink"
                        href={`mailto:support@glideapps.com`}
                        title="email support@glideapps.com"
                        target="_blank"
                        rel="noopener noreferrer">
                        support@glideapps.com
                    </a>{" "}
                    that includes a detailed explanation or a screen recording of the events that lead up to the error
                    message you have received. You can use{" "}
                    <a
                        className="quicklink"
                        href={`https://loom.com`}
                        title="https://loom.com"
                        target="_blank"
                        rel="noopener noreferrer">
                        loom.com
                    </a>{" "}
                    or any other screen recording software.
                </div>
            </div>
        </ModalBody>
    ),
};

export const SaveTaxDetailsError: ModalSnippet = {
    title: "Error - Could not save Tax details.",
    bodyContent: (
        <ModalBody>
            <div className="description">
                Please make sure that you have the a valid Tax Type and Tax ID for your Country by referring to{" "}
                <a
                    className="quicklink"
                    href={`https://www.notion.so/glideapps/TAX-IDs-62a468a6545a4c899eec9b42df885613`}
                    title="Glide Tax IDS Support Documentation"
                    target="_blank"
                    rel="noopener noreferrer">
                    our list of supported Tax ID types
                </a>{" "}
                If you continue to experience issues with adding Tax information to your app, please send an email to{" "}
                <a
                    className="quicklink"
                    href={`mailto:support@glideapps.com`}
                    title="email support@glideapps.com"
                    target="_blank"
                    rel="noopener noreferrer">
                    support@glideapps.com
                </a>{" "}
                that includes a screenshot of the error message you are receiving and relevant links.
            </div>
        </ModalBody>
    ),
};
